/**
 * The weekly Agapē (Agapēlla) Practice — the single recurring Sunday live that
 * every "Practice" surface refers to by one shared id: the home hero banner
 * (`Hero.tsx`), the practice landing screen (`AgapePracticeScreen.tsx`), and the
 * Agapē Center event card (`AgapeCenterScreen.tsx`).
 *
 * The id doubles as the local **reminder** id: the RSVP pill («Я иду» ↔ «Хочу!»)
 * and the calendar entry are keyed off it (`reminderIds.has(id)` /
 * `addToCalendar(id)`). All three surfaces must therefore agree on the value, or
 * the RSVP state desyncs between the banner and the practice page.
 *
 * Source of truth is the build-time env var:
 *   EXPO_PUBLIC_AGAPE_PRACTICE_LIVE_ID — the real Live.Id once the recurring
 *   weekly live has been seeded on the target stand. When set, RSVP subscribes
 *   against the real live; when unset (previews / pre-seed) we fall back to a
 *   fixed local-only placeholder so every surface keeps working before the live
 *   exists. Prod value is set per the CORE-75 spec after seeding.
 *
 * CORE-214 — replaces the `9999` literal that used to be hardcoded (and quietly
 * diverging) across the three files above.
 */

/** Local-only placeholder reminder id used before the real live is seeded. */
export const AGAPE_PRACTICE_LOCAL_REMINDER_ID = 9999;

/**
 * The real recurring-live id from the build env, or `null` when unset. A `null`
 * here is the signal to stay on the local-only reminder (no backend subscribe).
 */
export const AGAPE_PRACTICE_LIVE_ID: number | null = (() => {
  const raw = process.env.EXPO_PUBLIC_AGAPE_PRACTICE_LIVE_ID;
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
})();

/**
 * The id every Practice surface keys its RSVP / calendar reminder on — the real
 * live id when the env var is set, otherwise the local placeholder.
 */
export const AGAPE_PRACTICE_REMINDER_ID =
  AGAPE_PRACTICE_LIVE_ID ?? AGAPE_PRACTICE_LOCAL_REMINDER_ID;
